import type {
  SupportingDocumentItem,
  SupportingDocumentType,
} from "@/features/protest/services/steps.service";

export type LocalSupportingDocumentItem = {
  id: string;
  documentType: SupportingDocumentType;
  fileName: string;
  createdAt: string;
  sourceKey?: string;
  description?: string;
};

const STORAGE_PREFIX = "protest-supporting-documents";

function getStorageKey(propertyId: string): string {
  return `${STORAGE_PREFIX}:${propertyId.trim()}`;
}

function canUseStorage(): boolean {
  return typeof window !== "undefined" && Boolean(window.localStorage);
}

function readAll(propertyId: string): LocalSupportingDocumentItem[] {
  if (!canUseStorage() || !propertyId.trim()) return [];
  try {
    const raw = window.localStorage.getItem(getStorageKey(propertyId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((item): item is LocalSupportingDocumentItem =>
      Boolean(
        item &&
        typeof item === "object" &&
        typeof item.id === "string" &&
        typeof item.documentType === "string" &&
        typeof item.fileName === "string" &&
        typeof item.createdAt === "string" &&
        (typeof item.sourceKey === "string" || typeof item.sourceKey === "undefined")
      )
    );
  } catch {
    return [];
  }
}

function writeAll(propertyId: string, items: LocalSupportingDocumentItem[]): void {
  if (!canUseStorage() || !propertyId.trim()) return;
  window.localStorage.setItem(getStorageKey(propertyId), JSON.stringify(items));
}

export function getLocalSupportingDocuments(propertyId: string): LocalSupportingDocumentItem[] {
  return readAll(propertyId);
}

export function getLocalSupportingDocumentsByType(
  propertyId: string,
  documentType: SupportingDocumentType
): LocalSupportingDocumentItem[] {
  return readAll(propertyId).filter((item) => item.documentType === documentType);
}

// export function addLocalSupportingDocuments(
//   propertyId: string,
//   documentType: SupportingDocumentType,
//   fileNames: string[],
//   description: string
// ): LocalSupportingDocumentItem[] {
//   const current = readAll(propertyId);
//   const now = Date.now();
//   console.log("fileNamessadsadas", fileNames, description)
//   const additions = fileNames
//     .map((fileName) => fileName.trim())
//     .filter(Boolean)
//     .map((fileName, index) => ({
//       id: `${now}-${index}-${Math.random().toString(36).slice(2, 8)}`,
//       documentType,
//       fileName,
//       createdAt: new Date().toISOString(),
//       sourceKey: `local:${fileName.toLowerCase()}`,
//       description,
//     }));

//     console.log("additions", additions)

//   // if (additions.length === 0) return current;
//   const updated = [...additions, ...current];
//   writeAll(propertyId, updated);
//   return updated;
// }

export function addLocalSupportingDocuments(
  propertyId: string,
  documentType: SupportingDocumentType,
  description: string,
  fileNames?: string[]
): LocalSupportingDocumentItem[] {
  if (!description?.trim()) {
    throw new Error("Description is required");
  }

  const current = readAll(propertyId);
  const now = Date.now();

  // If no files, still create a record with description only
  if (!fileNames || fileNames.length === 0) {
    // Check if a description-only item already exists for this documentType
    const existingIndex = current.findIndex(
      (item) => item.documentType === documentType && item.sourceKey === "local:description-only"
    );

    if (existingIndex !== -1) {
      // Update existing entry
      const updated = [...current];

      const existingItem = updated[existingIndex];

      if (!existingItem) {
        throw new Error("Unexpected undefined item");
      }

      updated[existingIndex] = {
        ...existingItem,
        description,
        createdAt: new Date().toISOString(),
      };
      // updated[existingIndex] = {
      //   ...updated[existingIndex],
      //   description,
      //   createdAt: new Date().toISOString(), // or updatedAt if you track it
      // };

      writeAll(propertyId, updated);
      return updated;
    }

    // No existing record — create new
    const newItem: LocalSupportingDocumentItem = {
      id: `${now}-${Math.random().toString(36).slice(2, 8)}`,
      documentType,
      fileName: "",
      createdAt: new Date().toISOString(),
      sourceKey: `local:description-only`,
      description,
    };

    const updated = [newItem, ...current];
    writeAll(propertyId, updated);
    return updated;
  }

  const additions = fileNames
    .map((fileName) => fileName.trim())
    .filter(Boolean)
    .map((fileName, index) => ({
      id: `${now}-${index}-${Math.random().toString(36).slice(2, 8)}`,
      documentType,
      fileName,
      createdAt: new Date().toISOString(),
      sourceKey: `local:${fileName.toLowerCase()}`,
      description,
    }));

  console.log("additionsdfdsfwedfewwefwe", additions, current);

  const updated = [...additions, ...current];
  writeAll(propertyId, updated);
  return updated;
}

export function syncLocalSupportingDocumentsFromApi(
  propertyId: string,
  documentType: SupportingDocumentType,
  apiDocuments: SupportingDocumentItem[]
): LocalSupportingDocumentItem[] {
  const current = readAll(propertyId);
  const existingKeys = new Set(current.map((item) => item.sourceKey).filter(Boolean) as string[]);
  const existingNameKeys = new Set(
    current
      .filter((item) => item.documentType === documentType)
      .map((item) => `${item.documentType}:${item.fileName.toLowerCase()}`)
  );

  const additions: LocalSupportingDocumentItem[] = [];
  for (const doc of apiDocuments) {
    const fileName = (doc.file_name || doc.name || "").trim();
    if (!fileName) continue;
    const sourceKey = `api:${doc.id || doc.document_url || doc.url || fileName}`;
    const nameKey = `${documentType}:${fileName.toLowerCase()}`;
    if (existingKeys.has(sourceKey) || existingNameKeys.has(nameKey)) continue;

    additions.push({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      documentType,
      fileName,
      createdAt: new Date().toISOString(),
      sourceKey,
      description: doc.file_description,
    });
    existingKeys.add(sourceKey);
    existingNameKeys.add(nameKey);
  }

  if (additions.length === 0) return current;
  const updated = [...additions, ...current];
  writeAll(propertyId, updated);
  return updated;
}

/**
 * Overwrite local supporting-documents for a specific `documentType` with the latest API response.
 * - If API returns empty list, local entries for that type are removed.
 * - Other document types for the same property are preserved.
 */
export function overwriteLocalSupportingDocumentsFromApi(
  propertyId: string,
  documentType: SupportingDocumentType,
  apiDocuments: SupportingDocumentItem[]
): LocalSupportingDocumentItem[] {
  const current = readAll(propertyId);
  const preserved = current.filter((item) => item.documentType !== documentType);

  const nextForType: LocalSupportingDocumentItem[] = [];
  const seenNameKeys = new Set<string>();

  for (const doc of apiDocuments) {
    const fileName = (doc.file_name || doc.name || "").trim();
    if (!fileName) continue;
    const nameKey = `${documentType}:${fileName.toLowerCase()}`;
    if (seenNameKeys.has(nameKey)) continue;
    seenNameKeys.add(nameKey);

    nextForType.push({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      documentType,
      fileName,
      createdAt: new Date().toISOString(),
      sourceKey: `api:${doc.id || doc.document_url || doc.url || fileName}`,
    });
  }

  const updated = [...nextForType, ...preserved];
  writeAll(propertyId, updated);
  return updated;
}

export function removeLocalSupportingDocument(
  propertyId: string,
  id: string
): LocalSupportingDocumentItem[] {
  const updated = readAll(propertyId).filter((item) => item.id !== id);
  writeAll(propertyId, updated);
  return updated;
}
